import { Application, Container } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, SCREEN } from '../config/tokens';

/**
 * Screen geometry in reference pixels. The design surface is 1024 wide; on
 * taller phones the view grows downwards (`bottom` > 1536), on wide screens
 * it grows sideways (`left` < 0, `right` > 1024). HUD elements anchor to
 * `safeTop` / `safeBottom`, backgrounds cover `left..right` × `0..bottom`.
 */
export interface Layout {
  scale: number;
  left: number;
  right: number;
  bottom: number;
  /** first y below notch / status bar and the design's top protection zone */
  safeTop: number;
  /** last y above home indicator and the design's bottom protection zone */
  safeBottom: number;
}

export interface Screen {
  readonly view: Container;
  layout(l: Layout): void;
  destroy?(): void;
}

function deviceInsets() {
  let probe = document.getElementById('safe-probe');
  if (!probe) {
    probe = document.createElement('div');
    probe.id = 'safe-probe';
    document.body.appendChild(probe);
  }
  const cs = getComputedStyle(probe);
  return { top: parseFloat(cs.paddingTop) || 0, bottom: parseFloat(cs.paddingBottom) || 0 };
}

/**
 * Stops every running tween inside a display tree (before destroying it),
 * including tweens that sit inside timelines.
 */
export function killTweensDeep(c: Container): void {
  const targets = new Set<object>();
  const collect = (n: Container) => {
    targets.add(n).add(n.scale).add(n.position).add(n.pivot);
    for (const ch of n.children) collect(ch);
  };
  collect(c);
  for (const t of gsap.globalTimeline.getChildren(true, true, false)) {
    if ((t as gsap.core.Tween).targets().some((x) => targets.has(x as object))) t.kill();
  }
  gsap.killTweensOf([...targets]);
}

export class Stage {
  readonly app = new Application();
  /** everything lives in here, in reference pixels */
  readonly root = new Container();
  /** always above the current screen (purchase sheet, test video) */
  readonly top = new Container();
  private screen?: Screen;
  private current?: Layout;

  async init(host: HTMLElement): Promise<void> {
    await this.app.init({
      resizeTo: host,
      background: COLORS.mint,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(window.devicePixelRatio || 1, 3),
    });
    host.appendChild(this.app.canvas);
    this.app.stage.addChild(this.root);
    this.root.addChild(this.top);
    this.app.renderer.on('resize', () => this.relayout());
    this.relayout();
  }

  get layout(): Layout | undefined {
    return this.current;
  }

  /** Replaces the current screen with a short crossfade. */
  show(screen: Screen): void {
    const old = this.screen;
    if (old) {
      old.view.eventMode = 'none';
      gsap.to(old.view, {
        alpha: 0,
        duration: 0.2,
        onComplete: () => {
          this.root.removeChild(old.view);
          old.destroy?.();
          killTweensDeep(old.view);
          old.view.destroy({ children: true });
        },
      });
    }
    this.screen = screen;
    screen.view.alpha = 0;
    this.root.addChildAt(screen.view, this.root.getChildIndex(this.top));
    gsap.to(screen.view, { alpha: 1, duration: 0.25 });
    if (this.current) screen.layout(this.current);
  }

  private relayout(): void {
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    const scale = Math.min(w / SCREEN.width, h / SCREEN.height);
    const viewW = w / scale;
    const viewH = h / scale;
    const insets = deviceInsets();
    const offsetX = (viewW - SCREEN.width) / 2;

    this.root.scale.set(scale);
    this.root.position.set(offsetX * scale, 0);
    this.current = {
      scale,
      left: -offsetX,
      right: SCREEN.width + offsetX,
      bottom: viewH,
      safeTop: Math.max(SCREEN.safeTop, insets.top / scale),
      safeBottom: viewH - Math.max(SCREEN.safeBottom, insets.bottom / scale),
    };
    this.screen?.layout(this.current);
  }
}
